import { usePaiRequests } from 'containers/CompanyPai/CompanyPai.hooks';
import { useFeatureFlags } from 'containers/Navigation';
import { uniq } from 'lodash';
import { CompanyAssessmentsQuery_, GetReportsQuery_ } from 'models';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { stringToYear } from 'utils/date';
import { useCurrentCompany } from 'utils/hooks';

export enum AssessmentTypes {
  taxonomy = 'EU Taxonomy',
  pai = 'PAI',
  esrs = 'ESRS',
}

export type ReportItem = {
  id: string;
  status?: 'todo' | 'done';
  title: string;
  count?: number;
  type?: string;
  year?: number;
  period?: string;
  lastEdited?: number;
  onClick?: () => void;
};

export const getLatestTaxonomyDate = (
  assessment:
    | GetReportsQuery_['taxonomyReports'][number]
    | GetReportsQuery_['groupTaxonomyReports'][number]['consolidatedCompanyAssessment']
    | CompanyAssessmentsQuery_['assessments'][number]
) => {
  let max = assessment?.updatedAt;
  assessment?.bAssesssments.forEach((bu) => {
    if (bu.updatedAt > max) {
      max = bu.updatedAt;
    }
    bu.lastEditedFinancials.forEach((financial) => {
      if (financial.financials?.updatedAt > max) {
        max = financial.financials?.updatedAt;
      }
    });
    bu.activityReports.forEach((report) => {
      if (report.answers?.[0]?.updatedAt > max) {
        max = report.answers?.[0]?.updatedAt;
      }
    });
  });
  return max;
};

export const getLatestSRAssessmentDate = (
  assessment: GetReportsQuery_['sustainabilityReportingAssessments'][number]
): number | undefined => {
  const dates = [assessment.latestMaterialMetricUpdate, assessment.latestDatapointUpdate]
    .filter((date): date is string => date != null)
    .map((date) => new Date(date).getTime());

  return dates.length > 0 ? Math.max(...dates) : undefined;
};

const getLatestPaiDate = (assessment?: GetReportsQuery_['paiCompanyReports'][number]) => {
  let max = assessment?.updatedAt;
  if (assessment?.lastEditedAnswer?.[0]?.updatedAt > max) {
    max = assessment?.lastEditedAnswer?.[0]?.updatedAt;
  }
  return max ?? 0;
};

export const useHomeAssessments = (
  assessments?: GetReportsQuery_,
  companyId?: string,
  isGroup?: boolean
) => {
  const navigate = useNavigate();
  const { company } = useCurrentCompany();
  const {
    assessments: hasTaxonomyAccess,
    pai: hasPaiAccess,
    esrs: hasESRSAccess,
    vsme: hasVSMEAccess,
    ifrs: hasIFRSAccess,
    tcfd: hasTCFDAccess,
    esrsOilAndGas: hasESRSOilAndGasAccess,
    entitySpecific: hasEntitySpecificAccess,
    sasb: hasSASBAccess,
  } = useFeatureFlags({ company });
  const { requests } = usePaiRequests(companyId ?? undefined);

  const taxonomyReports = useMemo(() => {
    if (!hasTaxonomyAccess) {
      return [];
    }
    const taxonomy: ReportItem[] =
      assessments?.taxonomyReports?.map((report) => {
        return {
          id: report.id,
          status:
            (report.cachedResult?.progress?.financials === 100 &&
              report.cachedResult?.progress?.screening === 100) ||
            report.isLocked
              ? 'done'
              : 'todo',
          title: report.aggregate.title,
          type: AssessmentTypes.taxonomy,
          lastEdited: getLatestTaxonomyDate(report) ?? 0,
          year: stringToYear(report.startDate),
          onClick: () => navigate(`assessments/${report.id}`),
        };
      }) ?? [];

    const groupTaxonomy: ReportItem[] =
      assessments?.groupTaxonomyReports?.map((report) => ({
        id: report.id,
        status: report.consolidatedCompanyAssessment?.isLocked ? 'done' : 'todo',
        title: report.name,
        type: AssessmentTypes.taxonomy,
        lastEdited: getLatestTaxonomyDate(report.consolidatedCompanyAssessment) ?? 0,
        year: stringToYear(report.consolidatedCompanyAssessment?.startDate),
        onClick: () => navigate(`assessments/${report.id}`),
      })) ?? [];

    return isGroup ? groupTaxonomy : taxonomy;
  }, [assessments, isGroup, hasTaxonomyAccess]);

  const paiReports = useMemo(() => {
    if (!hasPaiAccess) {
      return [];
    }
    const pai: Array<ReportItem & { period?: string }> =
      (assessments &&
        requests?.map((r) => ({
          id: r.report?.id,
          title: String(r.year),
          period: r.period,
          year: r.year,
          type: AssessmentTypes.pai,
          lastEdited: getLatestPaiDate(r.report) ?? 0,
          onClick: () => navigate(`pai/${r.year}/year`),
          count: uniq(
            r.requests
              .map((request) => request.selectedIndicators)
              .flat()
              .filter(
                (s, _, array) =>
                  s.isNew === true &&
                  !array.some(
                    (a) => a.indicator.reference === s.indicator.reference && a.isNew === false
                  )
              )
              .map((selected) =>
                selected.indicator.investorMetrics
                  .map((metric) =>
                    metric.investorMetric.investeeMetrics.map(
                      (investee) => investee.metric.reference
                    )
                  )
                  .flat()
              )
              .flat()
          ).filter(
            (m) => r.report && r.report?.answers.every((a) => a.investeeMetricReference !== m)
          ).length,
        }))) ??
      [];

    return pai;
  }, [assessments, requests, hasPaiAccess]);

  const sustainabilityReportingAssessments = useMemo(() => {
    if (
      !hasESRSAccess &&
      !hasVSMEAccess &&
      !hasIFRSAccess &&
      !hasTCFDAccess &&
      !hasESRSOilAndGasAccess &&
      !hasEntitySpecificAccess &&
      !hasSASBAccess
    ) {
      return [];
    }
    const sustainabilityReporting: ReportItem[] =
      assessments?.sustainabilityReportingAssessments.map((assessment) => ({
        id: assessment.id,
        status: 'todo',
        title: String(assessment.reportingYear),
        type: assessment.frameworkRef?.toUpperCase(),
        year: assessment.reportingYear,
        lastEdited: getLatestSRAssessmentDate(assessment) ?? 0,
        onClick: () =>
          navigate(`sustainability-reporting/${assessment.frameworkRef}/${assessment.id}`),
      })) ?? [];
    return sustainabilityReporting;
  }, [
    assessments,
    hasESRSAccess,
    hasVSMEAccess,
    hasIFRSAccess,
    hasTCFDAccess,
    hasESRSOilAndGasAccess,
    hasEntitySpecificAccess,
    hasSASBAccess,
  ]);

  const allReports = useMemo(
    () =>
      [...taxonomyReports, ...paiReports, ...sustainabilityReportingAssessments].sort((a, b) => {
        const dateA = new Date(a.lastEdited ?? 0);
        const dateB = new Date(b.lastEdited ?? 0);
        return dateB.getTime() - dateA.getTime();
      }),
    [taxonomyReports, paiReports, sustainabilityReportingAssessments]
  );

  return allReports;
};
